import { useMutation } from "@tanstack/react-query";
import type { ReactNode } from "react";
import { FiActivity } from "react-icons/fi";

import { customerPing, type PingResult } from "@/lib/api";
import { msText } from "@/lib/bras";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useI18n } from "@/lib/i18n";
import { cn } from "@/lib/utils";

type Tone = "good" | "warn" | "crit";
const TEXT: Record<Tone, string> = { good: "text-signal-good", warn: "text-signal-warn", crit: "text-signal-crit" };

export function PingStats({ r }: { r: PingResult }) {
  const { t } = useI18n();
  const tone: Tone = r.loss_pct >= 100 ? "crit" : r.loss_pct > 0 ? "warn" : "good";
  const f = (label: string, v: ReactNode, cls?: string) => (
    <div><dt className="text-[11px] text-slate-500">{label}</dt><dd className={cn("data mt-0.5 text-[14px] text-slate-100", cls)}>{v}</dd></div>
  );
  return (
    <dl className="grid grid-cols-5 gap-3">
      {f(t("bras.loss"), `${r.loss_pct.toFixed(0)}%`, TEXT[tone])}
      {f(t("bras.recv"), `${r.received}/${r.sent}`)}
      {f("Min", msText(r.min_ms))}
      {f("Avg", msText(r.avg_ms))}
      {f("Max", msText(r.max_ms))}
    </dl>
  );
}


/**
 * "Ping from BRAS" for a subscriber or an ONT: the BRAS holding the customer's
 * PPPoE session pings the session address. It measures the access path even
 * where the ONT itself does not answer ping (VSOL).
 */
export default function CustomerPingDialog({ kind, id, name, onClose }: { kind: "subscribers" | "onts"; id: number; name: string; onClose: () => void }) {
  const { t } = useI18n();
  const run = useMutation({ mutationFn: () => customerPing(kind, id) });
  const d = run.data;
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{t("cping.title")}</DialogTitle>
          <DialogDescription className="text-slate-400">{name}</DialogDescription>
        </DialogHeader>
        {!d && !run.isPending && !run.error && <p className="text-[12.5px] text-slate-400">{t("cping.explain")}</p>}
        {run.isPending && <p className="text-[12.5px] text-slate-400">{t("cping.running")}</p>}
        {run.error && <p className="text-[12.5px] text-signal-crit">{(run.error as Error).message}</p>}
        {d?.session && (
          <div className="space-y-3">
            <p className="text-[12.5px] text-slate-400">{t("cping.found", { router: d.router, login: d.login })} <span className="data text-slate-100">{d.address}</span></p>
            <PingStats r={d.result} />
          </div>
        )}
        {d && !d.session && (
          <div className="space-y-2 text-[12.5px]">
            <p className="text-signal-warn">{t("cping.noSession", { logins: d.logins.join(", ") })}</p>
            <ul className="text-slate-400">
              {d.checked.map((c) => <li key={c.router}>{c.router}: {c.error ?? t("cping.notHere")}</li>)}
            </ul>
            {d.last_cpe_ip && <p className="text-slate-500">{t("cping.lastIp", { ip: d.last_cpe_ip })}</p>}
          </div>
        )}
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>{t("common.close")}</Button>
          <Button disabled={run.isPending} onClick={() => run.mutate()}><FiActivity /> {t(d ? "cping.again" : "cping.run")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
